import React, { useState, useEffect, useCallback } from 'react';
import { Heart, Send, Eye, EyeOff, Loader2, MessageCircleHeart, Settings, Lock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Skeleton } from '../../../components/Skeleton';
import { TextArea } from '../../../components/TextArea';
import { MilitarSelectField } from '../../../components/MilitarSelectField';
import { TiTiTinderCantada, TiTiTinderConfig } from '../../../types/tititinder';
import {
  getTiTiTinderConfig,
  setTiTiTinderAberto,
  enviarCantada,
  listarCantadas,
} from '../../../services/tiTiTinderService';
import { usePermissions, PERMISSOES } from '../../../hooks/usePermissions';
import { useToast } from '../../../hooks/use-toast';

function formatarData(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Tab: Enviar Cantada ───────────────────────────────────────────────────────

function TabEnviarCantada({ config }: { config: TiTiTinderConfig | null }) {
  const [destinatarioId, setDestinatarioId] = useState('');
  const [destinatarioNick, setDestinatarioNick] = useState('');
  const [cantada, setCantada] = useState('');
  const [anonimo, setAnonimo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!destinatarioId || !cantada.trim()) return;

    setIsSubmitting(true);
    try {
      await enviarCantada(destinatarioId, cantada.trim(), anonimo);
      toast({
        title: 'Cantada enviada!',
        description: anonimo
          ? `Sua cantada foi enviada anonimamente para ${destinatarioNick}.`
          : `Sua cantada foi enviada para ${destinatarioNick}.`,
      });
      setDestinatarioId('');
      setDestinatarioNick('');
      setCantada('');
      setAnonimo(false);
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? 'Erro ao enviar cantada.';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!config?.aberto) {
    return (
      <Card>
        <CardContent className="py-16 flex flex-col items-center gap-4 text-center">
          <Lock className="w-12 h-12 text-muted-foreground" />
          <div>
            <p className="font-semibold text-lg">TiTiTinder fechado</p>
            <p className="text-muted-foreground text-sm mt-1">
              O envio de cantadas está desabilitado no momento. Aguarde a próxima abertura.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageCircleHeart className="w-5 h-5 text-pink-500" />
          Enviar cantada
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <MilitarSelectField
            label="Destinatário"
            value={destinatarioId}
            onChange={(id, nick) => {
              setDestinatarioId(id);
              setDestinatarioNick(nick);
            }}
            placeholder="Selecione o militar..."
            required
          />

          <TextArea
            title="Cantada"
            value={cantada}
            onChange={(e) => setCantada(e.target.value)}
            placeholder="Escreva sua cantada..."
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground text-right -mt-2">{cantada.length}/500</p>

          <div className="flex items-center gap-3 rounded-lg border p-3 bg-muted/30">
            <Switch
              checked={anonimo}
              onCheckedChange={setAnonimo}
              id="anonimo-switch"
            />
            <div>
              <label htmlFor="anonimo-switch" className="text-sm font-medium cursor-pointer">
                Enviar como anônimo
              </label>
              <p className="text-xs text-muted-foreground">
                O destinatário não saberá quem enviou a cantada.
              </p>
            </div>
            {anonimo ? (
              <EyeOff className="w-4 h-4 text-muted-foreground ml-auto flex-shrink-0" />
            ) : (
              <Eye className="w-4 h-4 text-muted-foreground ml-auto flex-shrink-0" />
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!destinatarioId || !cantada.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar cantada
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Tab: Gerenciar Cantadas ───────────────────────────────────────────────────

interface TabGerenciarProps {
  config: TiTiTinderConfig | null;
  onConfigChange: (config: TiTiTinderConfig) => void;
}

function TabGerenciarCantadas({ config, onConfigChange }: TabGerenciarProps) {
  const [cantadas, setCantadas] = useState<TiTiTinderCantada[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTogglingAberto, setIsTogglingAberto] = useState(false);
  const { toast } = useToast();

  const loadCantadas = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listarCantadas();
      setCantadas(data);
    } catch {
      toast({ title: 'Erro', description: 'Falha ao carregar cantadas.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadCantadas();
  }, [loadCantadas]);

  const handleToggleAberto = async () => {
    setIsTogglingAberto(true);
    try {
      const novoAberto = !config?.aberto;
      const novoConfig = await setTiTiTinderAberto(novoAberto);
      onConfigChange({ aberto: novoConfig.aberto });
      toast({
        title: novoAberto ? 'TiTiTinder aberto!' : 'TiTiTinder fechado!',
        description: novoAberto
          ? 'Os militares já podem enviar cantadas.'
          : 'O envio de cantadas foi desabilitado.',
      });
    } catch {
      toast({ title: 'Erro', description: 'Falha ao alterar o status.', variant: 'destructive' });
    } finally {
      setIsTogglingAberto(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Card de controle aberto/fechado */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-sm">Status do TiTiTinder</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Habilite para permitir que os militares enviem cantadas.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Badge variant={config?.aberto ? 'default' : 'secondary'}>
                {config?.aberto ? 'Aberto' : 'Fechado'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleAberto}
                disabled={isTogglingAberto}
              >
                {isTogglingAberto && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {config?.aberto ? 'Fechar' : 'Abrir'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de cantadas */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Cantadas enviadas ({cantadas.length})
          </h2>
          <Button variant="ghost" size="sm" onClick={loadCantadas}>
            Atualizar
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : cantadas.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageCircleHeart className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">Nenhuma cantada enviada ainda.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {cantadas.map((c) => (
              <Card key={c.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">
                        {c.anonimo ? (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <EyeOff className="w-3.5 h-3.5" />
                            Anônimo
                          </span>
                        ) : (
                          <span>
                            {c.remetente?.nick ?? '—'}
                            {c.remetente?.patente && (
                              <span className="text-xs text-muted-foreground ml-1">
                                ({c.remetente.patente.patente})
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                      <span className="text-muted-foreground">→</span>
                      <span className="text-sm font-medium">
                        {c.destinatario?.nick ?? '—'}
                        {c.destinatario?.patente && (
                          <span className="text-xs text-muted-foreground ml-1">
                            ({c.destinatario.patente.patente})
                          </span>
                        )}
                      </span>

                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatarData(c.criado_em)}
                    </span>
                  </div>
                  <p className="text-sm bg-muted/30 rounded p-2 italic">"{c.cantada}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Página Principal ──────────────────────────────────────────────────────────

export function TiTiTinder() {
  const [config, setConfig] = useState<TiTiTinderConfig | null>(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);

  const { hasPermission } = usePermissions();
  const { toast } = useToast();

  const canVisualizar = hasPermission(PERMISSOES.TITITINDER_VISUALIZAR);
  const canGerenciar = hasPermission(PERMISSOES.TITITINDER_GERENCIAR);

  const loadConfig = useCallback(async () => {
    setIsConfigLoading(true);
    try {
      const data = await getTiTiTinderConfig();
      setConfig(data);
    } catch {
      toast({ title: 'Erro', description: 'Falha ao carregar status do TiTiTinder.', variant: 'destructive' });
    } finally {
      setIsConfigLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-pink-100 dark:bg-pink-950 flex items-center justify-center">
          <Heart className="w-5 h-5 text-pink-600 dark:text-pink-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">TiTiTinder</h1>
          <p className="text-muted-foreground text-sm">
            Envie cantadas para outros militares
            {!isConfigLoading && (
              <Badge
                variant={config?.aberto ? 'default' : 'secondary'}
                className="ml-2 text-xs"
              >
                {config?.aberto ? 'Aberto' : 'Fechado'}
              </Badge>
            )}
          </p>
        </div>
      </div>

      <Tabs defaultValue={canVisualizar ? 'enviar' : 'gerenciar'}>
        <TabsList>
          {canVisualizar && (
            <TabsTrigger value="enviar" className="gap-2">
              <Send className="w-4 h-4" />
              Enviar cantada
            </TabsTrigger>
          )}
          {canGerenciar && (
            <TabsTrigger value="gerenciar" className="gap-2">
              <Settings className="w-4 h-4" />
              Gerenciar
            </TabsTrigger>
          )}
        </TabsList>

        {canVisualizar && (
          <TabsContent value="enviar" className="mt-4">
            {isConfigLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <TabEnviarCantada config={config} />
            )}
          </TabsContent>
        )}

        {canGerenciar && (
          <TabsContent value="gerenciar" className="mt-4">
            {isConfigLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <TabGerenciarCantadas config={config} onConfigChange={setConfig} />
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
